import { ChevronDown, ChevronTop } from 'assets/icons';
import BadgeSquare from 'components/badge/badgeSquare';
import Button from 'components/button';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import { formatEuros } from 'components/invoiceList/invoiceAmount';
import { errorGroupTexts } from './invoiceJournalText';

// BadgeSquare type (its colour) and wording of each step.
const STEP_BADGES = {
  bestt: { type: 13, key: 'stepBestt', fallback: 'Étape 1 · Bestt' },
  pennylane: {
    type: 21,
    key: 'stepPennylane',
    fallback: 'Étape 2 · Pennylane',
  },
};

const STEP_LINES = {
  bestt: {
    one: [
      'groupBesttOne',
      '1 facture · bloquée à la récupération depuis Bestt',
    ],
    many: [
      'groupBesttMany',
      '{count} factures · bloquées à la récupération depuis Bestt',
    ],
  },
  pennylane: {
    one: ['groupPennylaneOne', '1 facture · bloquée à l’import dans Pennylane'],
    many: [
      'groupPennylaneMany',
      '{count} factures · bloquées à l’import dans Pennylane',
    ],
  },
};

const UNKNOWN_STEP_LINES = {
  one: ['groupOne', '1 facture'],
  many: ['groupMany', '{count} factures'],
};

/**
 * One cause of the "À corriger" tab (JBWEB-1280, mockup 2.8): what happened,
 * how to fix it, its invoices, and a retry of these invoices only.
 */
const JournalErrorGroup = ({
  group,
  text,
  isOpen,
  onToggle,
  onRetry,
  isRetryDisabled,
  onViewInvoice,
}) => {
  const { label, explanation, fix } = errorGroupTexts(text, group);
  const { invoices, count } = group;
  const badge = Object.prototype.hasOwnProperty.call(STEP_BADGES, group.step)
    ? STEP_BADGES[group.step]
    : null;
  const lines = badge ? STEP_LINES[group.step] : UNKNOWN_STEP_LINES;
  const [lineKey, lineFallback] = count === 1 ? lines.one : lines.many;

  const companyOf = (invoice) => {
    if (invoice.companyName) return invoice.companyName;
    if (invoice.besttClientId != null) {
      return fillText(text('besttClient', 'Client Bestt {id}'), {
        id: invoice.besttClientId,
      });
    }
    return '—';
  };

  return (
    <section className="rounded-lg border border-gray-200">
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900">{label}</h3>
          <div className="text-sm text-gray-500">
            {fillText(text(lineKey, lineFallback), { count })}
          </div>
        </div>
        {badge && (
          <BadgeSquare
            type={badge.type}
            size="xs"
            customLabel={text(badge.key, badge.fallback)}
          />
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={
            isOpen ? text('hideGroup', 'Replier') : text('showGroup', 'Déplier')
          }
          className="shrink-0 flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-50"
        >
          {isOpen ? (
            <ChevronTop wh={16} color="#374151" />
          ) : (
            <ChevronDown wh={16} color="#374151" />
          )}
        </button>
      </div>
      {isOpen && (
        <div className="flex flex-col gap-3 px-4 pb-4">
          <p className="text-sm text-gray-700">{explanation}</p>
          <div className="rounded-md bg-gray-100 px-3 py-2">
            <div className="text-sm font-medium text-gray-900">
              {text('howToFix', 'Comment corriger')}
            </div>
            <div className="text-sm text-gray-700">{fix}</div>
          </div>
          <ul className="flex flex-col divide-y divide-gray-100 border-t border-gray-100">
            {invoices.map((invoice) => {
              const canView = Boolean(invoice.companyId && invoice.pdfFileName);
              return (
                <li
                  key={invoice.number}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-gray-900">
                      {fillText(
                        text('invoiceLine', 'N° {number} · {company}'),
                        {
                          number: invoice.number,
                          company: companyOf(invoice),
                        },
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {typeof invoice.amountInclTax === 'number'
                        ? formatEuros(invoice.amountInclTax)
                        : '—'}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={!canView}
                    title={
                      canView
                        ? undefined
                        : text(
                            'pdfComesWithImport',
                            'Le PDF arrive avec l’import',
                          )
                    }
                    onClick={() => onViewInvoice(invoice)}
                    className="shrink-0 text-sm font-medium text-gray-900 underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                  >
                    {text('viewInvoice', 'Voir la facture')}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between gap-3">
            <code className="text-xs text-gray-500">
              {fillText(text('code', 'Code : {code}'), { code: group.code })}
            </code>
            <Button
              color="SECONDARY"
              size="SM"
              disabled={isRetryDisabled}
              onClick={() => onRetry(invoices.map((invoice) => invoice.number))}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {count === 1
                ? text('retryGroupOne', 'Réessayer cette facture')
                : fillText(
                    text('retryGroupMany', 'Réessayer ces {count} factures'),
                    { count },
                  )}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default JournalErrorGroup;
